<template>
  <div class="dossier-index">
    <b-overlay :show="loading" opacity="0.99">
      <!-- ─── Cabeçalho com progresso geral ─── -->
      <div class="dossier-index__header">
        <div class="dossier-index__header-left">
          <h2 class="dossier-index__title">
            <i class="fas fa-folder-open mr-2"></i>
            Dossiê Institucional
          </h2>
          <p class="dossier-index__subtitle">
            Organização documental para gestão e apresentação à fiscalização
          </p>
        </div>

        <div class="dossier-index__header-right">
          <!-- Progresso geral circular -->
          <div class="dossier-index__progress-ring" v-if="false">
            <svg width="72" height="72" viewBox="0 0 72 72">
              <circle
                cx="36"
                cy="36"
                r="30"
                fill="none"
                stroke="#e8e8e8"
                stroke-width="5"
              />
              <circle
                cx="36"
                cy="36"
                r="30"
                fill="none"
                :stroke="progressColor"
                stroke-width="5"
                stroke-linecap="round"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="strokeDashoffset"
                transform="rotate(-90 36 36)"
                style="transition: stroke-dashoffset 0.6s ease"
              />
            </svg>
            <div class="dossier-index__progress-label">
              <span class="dossier-index__progress-pct"
                >{{ progressPct }}%</span
              >
              <span class="dossier-index__progress-sub">completo</span>
            </div>
          </div>

          <!-- Contadores por estado -->
          <div class="dossier-index__counters">
            <div class="dossier-index__counter dossier-index__counter--ok">
              <i class="fas fa-check-circle"></i>
              <span>{{ counts.ok }}</span>
              <small>OK</small>
            </div>
            <div class="dossier-index__counter dossier-index__counter--warning">
              <i class="fas fa-exclamation-triangle"></i>
              <span>{{ counts.expiring + counts.expired }}</span>
              <small>Atenção</small>
            </div>
            <div
              class="dossier-index__counter dossier-index__counter--pending"
              v-if="false"
            >
              <i class="far fa-circle"></i>
              <span>{{ counts.pending }}</span>
              <small>Pendente</small>
            </div>
            <div
              class="dossier-index__counter dossier-index__counter--na"
              v-if="false"
            >
              <i class="fas fa-minus-circle"></i>
              <span>{{ counts.na }}</span>
              <small>N/A</small>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── Alertas de vencimento próximo ─── -->
      <div v-if="expiringAlerts.length > 0" class="dossier-index__alerts">
        <div class="dossier-index__alert-title">
          <i class="fas fa-bell mr-1"></i>
          Documentos que precisam de atenção
        </div>
        <div
          v-for="alert in expiringAlerts"
          :key="alert.moduleKey"
          class="dossier-index__alert-item"
          :class="`dossier-index__alert-item--${alert.type}`"
          @click="$emit('navigate', alert.moduleKey)"
        >
          <i :class="alert.icon" class="mr-2"></i>
          <span class="dossier-index__alert-name">{{ alert.label }}</span>
          <span class="dossier-index__alert-info">{{ alert.info }}</span>
          <i class="fas fa-chevron-right dossier-index__alert-arrow"></i>
        </div>
      </div>

      <div v-show="!permission('read', 'dossier_edit')">
        <h1 class="v-alert v-warning p-2 text-center">
          Você não tem permissão para alterar o dossiê!
        </h1>
      </div>

      <!-- ─── Seções de módulos ─── -->
      <div
        v-for="section in sections"
        :key="section.key"
        class="dossier-index__section"
        v-show="
          permission('read', 'dossier_edit') &&
          section.modules.some((section_module) =>
            isSection(section_module.component)
          )
        "
      >
        <!-- Cabeçalho da seção -->
        <div class="dossier-index__section-header">
          <i :class="section.icon" class="dossier-index__section-icon"></i>
          <h3 class="dossier-index__section-title">{{ section.label }}</h3>
          <!-- Mini barra de progresso da seção -->
          <div class="dossier-index__section-progress">
            <div class="dossier-index__section-progress-bar">
              <div
                class="dossier-index__section-progress-fill"
                :style="{ width: sectionProgress(section) + '%' }"
                :class="sectionProgressClass(section)"
              ></div>
            </div>
            <span class="dossier-index__section-progress-text">
              {{ sectionDoneCount(section) }}/{{ section.modules.length }}
            </span>
          </div>
        </div>

        <!-- Grid de módulos da seção -->
        <div class="dossier-index__modules">
          <div
            v-for="mod in section.modules"
            :key="mod.key"
            class="dossier-index__module-card"
            :class="[
              `dossier-index__module-card--${moduleState(mod)}`,
              { 'dossier-index__module-card--clickable': true },
            ]"
            @click="$emit('navigate', mod.key)"
            v-show="isSection(mod.component)"
          >
            <!-- Ícone de estado (canto superior direito) -->
            <span
              class="dossier-index__module-state"
              :class="`dossier-index__module-state--${moduleState(mod)}`"
            >
              <i :class="moduleStateIcon(mod)"></i>
            </span>

            <!-- Ícone do módulo -->
            <div class="dossier-index__module-icon">
              <i :class="mod.icon"></i>
            </div>

            <!-- Info -->
            <div class="dossier-index__module-info">
              <span class="dossier-index__module-label">{{ mod.label }}</span>
              <span class="dossier-index__module-status">
                {{ moduleStatusText(mod) }}
              </span>
            </div>

            <!-- Validade (quando houver) -->
            <div
              v-if="moduleValidityDate(mod)"
              class="dossier-index__module-validity"
              :class="moduleValidityClass(mod)"
            >
              <i class="fas fa-calendar-alt mr-1"></i>
              {{ moduleValidityDate(mod) }}
            </div>

            <!-- Seta de navegação -->
            <i class="fas fa-chevron-right dossier-index__module-arrow"></i>
          </div>
        </div>
      </div>

      <div
        v-show="
          permission('read', 'dossier_edit') &&
          permission('read', 'dossier_work_schedule') &&
          isSection('DossierWorkSchedule')
        "
        class="dossier-index__section"
      >
        <DossierWorkSchedule />
      </div>

      <div
        v-show="
          permission('read', 'dossier_edit') &&
          permission('read', 'dossier_menu') &&
          isSection('DossierMenu')
        "
        class="dossier-index__section"
      >
        <DossierMenu />
      </div>

      <!-- ─── Estado vazio (nunca preencheu nada) ─── -->
      <div v-if="!loading && totalModules === 0" class="dossier-index__empty">
        <i class="fas fa-folder-open dossier-index__empty-icon"></i>
        <p>Nenhum dado carregado. Tente recarregar a página.</p>
        <v-btn small class="btn-add mt-2" @click="retrieve">
          <i class="fas fa-sync-alt mr-1"></i> Recarregar
        </v-btn>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import DossierWorkSchedule from "../../views/InstitutionalDossier/WorkSchedule.vue";
import DossierMenu from "../../views/InstitutionalDossier/Menu.vue";

import api from "../../services/api";
import permission from "../../lib/permission";

const DEFAULT_INFORMATIONS = [
  {
    key: "DossierTechnicalManager",
    label: "Responsável Técnico",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierLicense",
    label: "Alvará / Licença de Funcionamento",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierSanitaryLicense",
    label: "Licença Sanitária",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierSanitaryVisit",
    label: "Termo de Visita Sanitária",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierFireDept",
    label: "Certificado do Corpo de Bombeiros",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierElderlyCouncil",
    label: "Inscrição no Conselho Municipal da Pessoa Idosa (CMI / CMDPI)",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWelfareCouncil",
    label: "Registro no Conselho Municipal de Assistência Social (CMAS)",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWaterTank",
    label: "Limpeza dos Reservatórios de Água",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWaterPotability",
    label: "Laudo de Potabilidade da Água",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierPestControl",
    label: "Controle de Pragas e Vetores",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierOutsourcing",
    label: "Terceirização de Serviços",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierHealthProviders",
    label: "Prestadores de Saúde",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierHRClt",
    label: "Recursos Humanos – Colaboradores CLT",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierInfrastructure",
    label: "Infraestrutura da ILPI",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierSOPs",
    label: "POPs – Procedimentos Operacionais Padrão",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWorkSchedule",
    label: "Escalas de Trabalho",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierMenu",
    label: "Cardápios",
    selected: true,
    expanded: false,
    fields: [],
  },
];

// ─── Definição completa dos módulos agrupados por seção ──────────────────────
// moduleKey deve bater exatamente com as chaves retornadas pelo /summary
const SECTIONS = [
  {
    key: "institucional",
    label: "Documentação Institucional",
    icon: "fas fa-building",
    modules: [
      { key: "cnpj", label: "CNPJ", icon: "fas fa-id-card", component: "CNPJ" },
      {
        key: "technical_manager",
        label: "Responsável Técnico",
        icon: "fas fa-user-md",
        component: "DossierTechnicalManager",
      },
      {
        key: "license",
        label: "Alvará de Funcionamento",
        icon: "fas fa-stamp",
        component: "DossierLicense",
      },
    ],
  },
  {
    key: "sanitaria",
    label: "Vigilância Sanitária",
    icon: "fas fa-shield-alt",
    modules: [
      {
        key: "sanitary_license",
        label: "Licença Sanitária",
        icon: "fas fa-certificate",
        component: "DossierSanitaryLicense",
      },
      {
        key: "sanitary_visit",
        label: "Termo de Visita Sanitária",
        icon: "fas fa-clipboard-check",
        component: "DossierSanitaryVisit",
      },
      {
        key: "water_tank",
        label: "Limpeza dos Reservatórios",
        icon: "fas fa-tint",
        component: "DossierWaterTank",
      },
      {
        key: "water_potability",
        label: "Laudo de Potabilidade",
        icon: "fas fa-glass-water",
        component: "DossierWaterPotability",
      },
      {
        key: "pest_control",
        label: "Controle de Pragas",
        icon: "fas fa-bug",
        component: "DossierPestControl",
      },
    ],
  },
  {
    key: "seguranca",
    label: "Segurança",
    icon: "fas fa-fire-extinguisher",
    modules: [
      {
        key: "fire_dept",
        label: "Corpo de Bombeiros",
        icon: "fas fa-fire",
        component: "DossierFireDept",
      },
    ],
  },
  {
    key: "conselhos",
    label: "Conselhos e Registros",
    icon: "fas fa-balance-scale",
    modules: [
      {
        key: "elderly_council",
        label: "Conselho do Idoso (CMI)",
        icon: "fas fa-users",
        component: "DossierElderlyCouncil",
      },
      {
        key: "welfare_council",
        label: "Conselho de Assistência (CMAS)",
        icon: "fas fa-hands-helping",
        component: "DossierWelfareCouncil",
      },
    ],
  },
  {
    key: "rh",
    label: "Recursos Humanos",
    icon: "fas fa-user-tie",
    modules: [
      {
        key: "hr_clt",
        label: "Colaboradores CLT",
        icon: "fas fa-id-badge",
        component: "DossierHRClt",
      },
      {
        key: "health_providers",
        label: "Prestadores de Saúde",
        icon: "fas fa-stethoscope",
        component: "DossierHealthProviders",
      },
    ],
  },
  {
    key: "operacional",
    label: "Gestão Operacional",
    icon: "fas fa-tasks",
    modules: [
      {
        key: "outsourcing",
        label: "Terceirização de Serviços",
        icon: "fas fa-handshake",
        component: "DossierOutsourcing",
      },
      {
        key: "infrastructure",
        label: "Infraestrutura",
        icon: "fas fa-home",
        component: "DossierInfrastructure",
      },
      {
        key: "sops",
        label: "POPs",
        icon: "fas fa-book-open",
        component: "DossierSOPs",
      },
    ],
  },
];

// ─── Quantos dias faltam para uma data ───────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function formatDateBR(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
}

export default {
  name: "DossierIndex",

  components: {
    DossierWorkSchedule,
    DossierMenu,
  },

  props: {
    // Permite que o pai injete os dados summary diretamente
    // (evita duplo fetch se o pai já carregou)
    summaryData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      summary: {}, // { cnpj: { status, validity_date, not_applicable, file_url }, ... }
      sections: SECTIONS,
      circumference: 2 * Math.PI * 30, // raio 30

      configMap: {},
    };
  },

  computed: {
    // ── Estado por módulo ────────────────────────────────────────────────────
    // Retorna: 'ok' | 'expiring' | 'expired' | 'pending' | 'na'

    allModules() {
      return SECTIONS.flatMap((s) => s.modules);
    },

    totalModules() {
      return this.allModules.length;
    },

    // Contadores por estado
    counts() {
      const result = { ok: 0, expiring: 0, expired: 0, pending: 0, na: 0 };
      this.allModules.forEach((mod) => {
        result[this.moduleState(mod)]++;
      });
      return result;
    },

    // Progresso: ok + expiring + na contam como "tratados"
    progressPct() {
      if (!this.totalModules) return 0;
      const done = this.counts.ok + this.counts.expiring + this.counts.na;
      return Math.round((done / this.totalModules) * 100);
    },

    strokeDashoffset() {
      return this.circumference - (this.progressPct / 100) * this.circumference;
    },

    progressColor() {
      if (this.progressPct === 100) return "#30915b";
      if (this.progressPct >= 70) return "#1f5a76";
      if (this.progressPct >= 40) return "#fb8b01";
      return "#dc3545";
    },

    // ── Alertas de atenção ───────────────────────────────────────────────────
    expiringAlerts() {
      const alerts = [];
      this.allModules.forEach((mod) => {
        const state = this.moduleState(mod);
        if (state === "expired") {
          alerts.push({
            moduleKey: mod.key,
            label: mod.label,
            icon: "fas fa-times-circle",
            type: "expired",
            info: `Vencido em ${this.moduleValidityDate(mod)}`,
          });
        } else if (state === "expiring") {
          const days = daysUntil(this.summary[mod.key]?.validity_date);
          alerts.push({
            moduleKey: mod.key,
            label: mod.label,
            icon: "fas fa-exclamation-triangle",
            type: "expiring",
            info: `Vence em ${days} dia${days !== 1 ? "s" : ""}`,
          });
        }
      });
      return alerts;
    },
  },

  watch: {
    summaryData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.summary = val;
        } else {
          this.retrieve();
        }
      },
    },
  },

  methods: {
    permission,

    // ── Carrega summary da API ───────────────────────────────────────────────
    async retrieve() {
      try {
        this.loading = true;
        const response = await api.get("/rest-home/dossier/summary");
        const { error, data } = response.data;
        if (!error && data) {
          this.summary = data;
        }
      } catch (err) {
        console.error("DossierIndex.retrieve:", err);
      } finally {
        this.loading = false;
      }

      try {
        const res = await api.get("/rest-home/settings/config-dossier");

        if (!res.data.error) {
          let saved = res.data.informations;

          if (saved) {
            if (typeof saved === "string") {
              saved = JSON.parse(saved);
              if (typeof saved === "string") saved = JSON.parse(saved);
            }

            const merged = this.mergeSections(DEFAULT_INFORMATIONS, saved);
            this.configMap = this.buildConfigMap(merged);
          } else {
            this.configMap = this.buildConfigMap(DEFAULT_INFORMATIONS);
          }
        } else {
          this.configMap = this.buildConfigMap(DEFAULT_INFORMATIONS);
        }
      } catch (err) {
        this.configMap = this.buildConfigMap(DEFAULT_INFORMATIONS);
        console.error("retrieve: erro no /menu →", err);
      }
    },

    // ── Estado de um módulo ──────────────────────────────────────────────────
    moduleState(mod) {
      const data = this.summary[mod.key];
      if (!data) return "pending";

      // Não aplicável
      if (data.not_applicable) return "na";

      // Sem status preenchido
      const hasStatus = Array.isArray(data.status)
        ? data.status.length > 0
        : !!data.status;

      if (!hasStatus) return "pending";

      // Verifica validade
      if (data.validity_date) {
        const days = daysUntil(data.validity_date);
        if (days !== null && days <= 0) return "expired";
        if (days !== null && days <= 30) return "expiring";
      }

      return "ok";
    },

    moduleStateIcon(mod) {
      const map = {
        ok: "fas fa-check-circle",
        expiring: "fas fa-exclamation-triangle",
        expired: "fas fa-times-circle",
        pending: "far fa-circle",
        na: "fas fa-minus-circle",
      };
      return map[this.moduleState(mod)] || "far fa-circle";
    },

    moduleStatusText(mod) {
      const data = this.summary[mod.key];
      if (!data) return "Não preenchido";
      if (data.not_applicable) return "Não aplicável";

      const state = this.moduleState(mod);
      if (state === "pending") return "Pendente";
      if (state === "expired") return "Documento vencido";
      if (state === "expiring") return "Vence em breve";

      // Tenta exibir o label do primeiro status selecionado
      const status = Array.isArray(data.status) ? data.status[0] : data.status;
      if (!status) return "Preenchido";

      const labels = {
        possui_vigente: "Vigente",
        possui_provisorio: "Provisório",
        possui_atualizado: "Atualizado",
        possui_desatualizado: "Desatualizado",
        em_elaboracao: "Em elaboração",
        em_renovacao: "Em renovação",
        nao_possui: "Não possui",
        nao_exigido: "Não exigido",
      };
      return labels[status] || "Preenchido";
    },

    moduleValidityDate(mod) {
      const data = this.summary[mod.key];
      if (!data?.validity_date) return null;
      return formatDateBR(data.validity_date);
    },

    moduleValidityClass(mod) {
      const state = this.moduleState(mod);
      if (state === "expired") return "dossier-index__module-validity--expired";
      if (state === "expiring")
        return "dossier-index__module-validity--expiring";
      return "dossier-index__module-validity--ok";
    },

    // ── Progresso por seção ──────────────────────────────────────────────────
    sectionDoneCount(section) {
      return section.modules.filter((mod) => {
        const state = this.moduleState(mod);
        return state === "ok" || state === "expiring" || state === "na";
      }).length;
    },

    sectionProgress(section) {
      if (!section.modules.length) return 0;
      return Math.round(
        (this.sectionDoneCount(section) / section.modules.length) * 100
      );
    },

    sectionProgressClass(section) {
      const pct = this.sectionProgress(section);
      if (pct === 100) return "dossier-index__section-progress-fill--ok";
      if (pct >= 50) return "dossier-index__section-progress-fill--partial";
      return "dossier-index__section-progress-fill--low";
    },

    buildConfigMap(sections) {
      const map = {};
      sections.forEach((section) => {
        const fieldsMap = {};
        if (section.fields) {
          section.fields.forEach((f) => {
            fieldsMap[f.key] = f.selected;
          });
        }
        map[section.key] = {
          selected: section.selected,
          fields: fieldsMap,
        };
      });
      return map;
    },

    mergeSections(defaults, saved) {
      return defaults.map((defaultSection) => {
        const savedSection = saved.find((s) => s.key === defaultSection.key);
        if (!savedSection) return { ...defaultSection };

        const mergedFields = defaultSection.fields.map((defaultField) => {
          const savedField = savedSection.fields
            ? savedSection.fields.find((f) => f.key === defaultField.key)
            : null;
          return savedField
            ? { ...defaultField, selected: savedField.selected }
            : { ...defaultField };
        });

        return {
          ...defaultSection,
          selected: savedSection.selected,
          fields: mergedFields,
        };
      });
    },

    isSection(sectionKey) {
      if (!this.configMap[sectionKey]) return true; // sem config → exibe
      return this.configMap[sectionKey].selected === true;
    },

    isField(sectionKey, fieldKey) {
      if (!this.isSection(sectionKey)) return false;
      const section = this.configMap[sectionKey];
      if (!section || section.fields[fieldKey] === undefined) return true; // sem config → exibe
      return section.fields[fieldKey] === true;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER INDEX
   Índice geral do Dossiê com status visual de todos os módulos.
   ═══════════════════════════════════════════════════════════════ */

.dossier-index {
  width: 100%;
}

/* ── Cabeçalho ───────────────────────────────────────────────── */
.dossier-index__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 20px 24px;
  margin-bottom: 16px;
}

.dossier-index__header-left {
  flex: 1;
  min-width: 0;
}

.dossier-index__title {
  font-size: 18px;
  font-weight: 700;
  color: #1f5a76;
  margin: 0 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
}

.dossier-index__subtitle {
  font-size: 13px;
  color: #777;
  margin: 0;
}

.dossier-index__header-right {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
}

/* ── Anel de progresso ───────────────────────────────────────── */
.dossier-index__progress-ring {
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.dossier-index__progress-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.dossier-index__progress-pct {
  font-size: 14px;
  font-weight: 700;
  color: #1f5a76;
  line-height: 1;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
}

.dossier-index__progress-sub {
  font-size: 9px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}

/* ── Contadores ──────────────────────────────────────────────── */
.dossier-index__counters {
  display: flex;
  gap: 12px;
}

.dossier-index__counter {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 36px;
}

.dossier-index__counter i {
  font-size: 16px;
}

.dossier-index__counter span {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
}

.dossier-index__counter small {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #888;
}

.dossier-index__counter--ok i,
.dossier-index__counter--ok span {
  color: #30915b;
}
.dossier-index__counter--warning i,
.dossier-index__counter--warning span {
  color: #fb8b01;
}
.dossier-index__counter--pending i,
.dossier-index__counter--pending span {
  color: #ccc;
}
.dossier-index__counter--na i,
.dossier-index__counter--na span {
  color: #bbb;
}

/* ── Alertas de atenção ──────────────────────────────────────── */
.dossier-index__alerts {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 16px;
  overflow: hidden;
}

.dossier-index__alert-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  padding: 10px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
}

.dossier-index__alert-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f4f4f4;
  transition: background 0.15s;
  font-size: 13px;
}

.dossier-index__alert-item:last-child {
  border-bottom: none;
}

.dossier-index__alert-item:hover {
  background: #fafafa;
}

.dossier-index__alert-item--expired {
  border-left: 3px solid #dc3545;
}
.dossier-index__alert-item--expired i {
  color: #dc3545;
}

.dossier-index__alert-item--expiring {
  border-left: 3px solid #fb8b01;
}
.dossier-index__alert-item--expiring i {
  color: #fb8b01;
}

.dossier-index__alert-name {
  font-weight: 600;
  color: #333;
  flex: 1;
}

.dossier-index__alert-info {
  font-size: 12px;
  color: #888;
}

.dossier-index__alert-arrow {
  font-size: 11px;
  color: #ccc;
}

/* ── Seções ──────────────────────────────────────────────────── */
.dossier-index__section {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
}

.dossier-index__section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
}

.dossier-index__section-icon {
  font-size: 14px;
  color: #1f5a76;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}

.dossier-index__section-title {
  font-size: 12px;
  font-weight: 700;
  color: #33495e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex: 1;
}

/* Progresso da seção */
.dossier-index__section-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dossier-index__section-progress-bar {
  width: 80px;
  height: 4px;
  background: #e8e8e8;
  border-radius: 2px;
  overflow: hidden;
}

.dossier-index__section-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.dossier-index__section-progress-fill--ok {
  background: #30915b;
}
.dossier-index__section-progress-fill--partial {
  background: #fb8b01;
}
.dossier-index__section-progress-fill--low {
  background: #e0e0e0;
}

.dossier-index__section-progress-text {
  font-size: 11px;
  color: #888;
  flex-shrink: 0;
}

/* ── Grid de módulos ─────────────────────────────────────────── */
.dossier-index__modules {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0;
}

/* ── Card de módulo ──────────────────────────────────────────── */
.dossier-index__module-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px 12px;
  border-right: 1px solid #f0f0ef;
  border-bottom: 1px solid #f0f0ef;
  transition: background 0.15s;
  min-height: 100px;
}

.dossier-index__module-card--clickable {
  cursor: pointer;
}

.dossier-index__module-card--clickable:hover {
  background: #fafafa;
}

/* Borda esquerda colorida por estado */
.dossier-index__module-card--ok {
  border-left: 3px solid #30915b;
}
.dossier-index__module-card--expiring {
  border-left: 3px solid #fb8b01;
}
.dossier-index__module-card--expired {
  border-left: 3px solid #dc3545;
}
.dossier-index__module-card--pending {
  border-left: 3px solid #e0e0e0;
}
.dossier-index__module-card--na {
  border-left: 3px solid #ddd;
}

/* Ícone de estado (canto superior direito) */
.dossier-index__module-state {
  position: absolute;
  top: 10px;
  right: 32px;
  font-size: 14px;
}

.dossier-index__module-state--ok {
  color: #30915b;
}
.dossier-index__module-state--expiring {
  color: #fb8b01;
}
.dossier-index__module-state--expired {
  color: #dc3545;
}
.dossier-index__module-state--pending {
  color: #ddd;
}
.dossier-index__module-state--na {
  color: #ccc;
}

/* Ícone do módulo */
.dossier-index__module-icon {
  font-size: 20px;
  color: #1f5a76;
  opacity: 0.6;
  margin-bottom: 2px;
}

.dossier-index__module-card--na .dossier-index__module-icon,
.dossier-index__module-card--pending .dossier-index__module-icon {
  opacity: 0.25;
}

/* Info */
.dossier-index__module-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.dossier-index__module-label {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.dossier-index__module-card--na .dossier-index__module-label,
.dossier-index__module-card--pending .dossier-index__module-label {
  color: #999;
}

.dossier-index__module-status {
  font-size: 11px;
  color: #888;
}

.dossier-index__module-card--ok .dossier-index__module-status {
  color: #30915b;
}
.dossier-index__module-card--expiring .dossier-index__module-status {
  color: #fb8b01;
}
.dossier-index__module-card--expired .dossier-index__module-status {
  color: #dc3545;
  font-weight: 600;
}

/* Validade */
.dossier-index__module-validity {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
}

.dossier-index__module-validity--ok {
  background: #d4edda;
  color: #155724;
}
.dossier-index__module-validity--expiring {
  background: #fff3cd;
  color: #856404;
}
.dossier-index__module-validity--expired {
  background: #f8d7da;
  color: #842029;
}

/* Seta de navegação */
.dossier-index__module-arrow {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  font-size: 11px;
  color: #ddd;
  transition: color 0.15s;
}

.dossier-index__module-card:hover .dossier-index__module-arrow {
  color: #1f5a76;
}

/* ── Estado vazio ────────────────────────────────────────────── */
.dossier-index__empty {
  text-align: center;
  padding: 48px 24px;
  color: #aaa;
}

.dossier-index__empty-icon {
  font-size: 48px;
  display: block;
  color: #ddd;
  margin-bottom: 12px;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 700px) {
  .dossier-index__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .dossier-index__header-right {
    width: 100%;
    justify-content: space-between;
  }

  .dossier-index__modules {
    grid-template-columns: 1fr 1fr;
  }

  .dossier-index__section-progress {
    display: none;
  }
}

@media (max-width: 400px) {
  .dossier-index__modules {
    grid-template-columns: 1fr;
  }

  .dossier-index__counters {
    gap: 8px;
  }
}
</style>